import React, { FunctionComponent, useEffect } from 'react'
import { ReactComponent as ColonyArt } from '../../assets/img/colony-art.svg'
import { HubStats, compact } from './hubBrand'
import { prefersReducedMotion, useCountUp } from './countUp'
import styles from './ColonyScene.module.css'

/*
 * Colony: an alternative Intelligence Hub banner (demo option). The crew of a space station at its three
 * windows, each at work on one of the team's totals: a robot showing a crew member a warning about the
 * high-risk customers (left); a crew member watching print volume and fleet value on the wall screens
 * (middle); and, at the right window, someone coding while a little robot trundles about the devices, with
 * satellites floating outside. A shooting star crosses all three windows. The totals sit either side of the
 * station, counting up.
 *
 * The station is a supplied illustration (colony-art.svg), with its moving parts grouped under colart-* ids;
 * the screens' charts, code and warning, and the shooting star, are drawn over it here.
 */

interface Props {
  stats?: HubStats
  onDone: () => void
}

interface Stat {
  key: string
  label: string
  description: string
  value?: number
  prefix?: string
}

const customersText = (n?: number) => (n === undefined ? 'your customers' : `${compact(n)} customers`)

/* left of the station, then right */
const statsOf = (s?: HubStats): [Stat[], Stat[]] => [
  [
    { key: 'pages', label: 'Print volume', description: 'Pages printed last month', value: s?.printVolume },
    { key: 'risk', label: 'Risk flags', description: 'Customers at high renewal risk', value: s?.riskFlags },
  ],
  [
    {
      key: 'value',
      label: 'Est. fleet value',
      description: `Across ${customersText(s?.customers)}`,
      value: s?.fleetValue,
      prefix: '$',
    },
    { key: 'devices', label: 'Devices', description: `Across ${customersText(s?.customers)}`, value: s?.devices },
  ],
]

/* when the last total has counted up */
const SEQUENCE_MS = 3200

/* the illustration's size, and its three windows: the shooting star only shows through them */
const ART_W = 2736
const ART_H = 893
const WINDOWS = [
  'M806.6 113.1L721.6 674.6C712.2 737 653.9 787.7 591.4 787.7L161.4 787.7C99 787.7 56 737 65.5 674.6L150.4 113.1C159.8 50.6 218.1 0 280.6 0L710.6 0C755.8 0 790.8 26.5 803.1 64.8C807.8 79.5 809.2 95.8 806.6 113.1Z',
  'M1827.2 787.7L970.9 787.7C908.4 787.7 865.4 737 874.9 674.6L959.8 113.1C969.3 50.6 1027.6 0 1090 0L1708 0C1770.5 0 1828.8 50.6 1838.3 113.1L1923.2 674.6C1932.6 737 1889.7 787.7 1827.2 787.7Z',
  'M2636.7 787.7L2206.7 787.7C2144.2 787.7 2085.9 737 2076.5 674.6L1991.5 113.1C1982.1 50.6 2025.1 0 2087.6 0L2517.5 0C2580 0 2638.3 50.6 2647.7 113.1L2732.7 674.6C2742.1 737 2699.1 787.7 2636.7 787.7Z',
]

/* the screens in the illustration (each 168 × 132) */
const LINE_SCREEN = { x: 1302, y: 122 }
const BAR_SCREEN = { x: 1482, y: 122 }
const CODE_SCREEN = { x: 2204, y: 460 }
/* the screen the robot at the left holds up (73 × 93) */
const TABLET = { x: 175, y: 349, w: 73, h: 93 }
const SCREEN_W = 168
const SCREEN_H = 132
const PAD = 22

/* when there's no data to draw: a gentle rise, and a few bars */
const SAMPLE_LINE = [900, 1200, 1000, 1700, 1500, 2100]
const SAMPLE_BARS = [38, 60, 47, 80, 66]

/* the code: each line's indent and token widths, in screen units */
const CODE_LINES: { indent: number; tokens: number[] }[] = [
  { indent: 0, tokens: [34, 52] },
  { indent: 12, tokens: [22, 40, 18] },
  { indent: 24, tokens: [48, 26] },
  { indent: 24, tokens: [30, 44] },
  { indent: 12, tokens: [16] },
  { indent: 0, tokens: [40, 30] },
  { indent: 12, tokens: [26, 50] },
]
/* the tokens' whites, by position on the line */
const TOKEN_SHADES = [styles.codeBright, styles.codeDim, styles.codeMid]

const StatBlock: FunctionComponent<{ stat: Stat; index: number }> = ({ stat, index }) => {
  const n = useCountUp(stat.value, 600 + index * 250)
  const format = (v?: number) => (v === undefined ? '—' : `${stat.prefix ?? ''}${compact(Math.round(v))}`)
  // on its way up to a total in the tens of thousands it passes through full numbers (9,876) before 24K, which
  // are wider: room is kept for whichever is widest
  const widest = [format(stat.value), (stat.value ?? 0) >= 10000 ? format(9999) : ''].reduce((a, b) =>
    b.length > a.length ? b : a
  )
  return (
    <div className={`${styles.stat} ${styles[`s${index}`]}`}>
      {/* the final number, unseen, holds the space, so the words beside it stay put while it counts up */}
      <span className={styles.value}>
        <span className={styles.valueFinal} aria-hidden='true'>
          {widest}
        </span>
        <span className={styles.valueCounting}>{format(n)}</span>
      </span>
      <span className={styles.statText}>
        <span className={styles.label}>{stat.label}</span>
        <span className={styles.description}>{stat.description}</span>
      </span>
    </div>
  )
}

/* a smooth curve through the points (Catmull–Rom, as cubic Béziers) */
const smoothPath = (points: number[][]): string => {
  const f = (n: number) => n.toFixed(1)
  return points.reduce((d, [x, y], i) => {
    if (i === 0) return `M${f(x)} ${f(y)}`
    const [x0, y0] = points[i - 2] ?? points[i - 1]
    const [x1, y1] = points[i - 1]
    const [x3, y3] = points[i + 1] ?? [x, y]
    const c1 = [x1 + (x - x0) / 6, y1 + (y - y0) / 6]
    const c2 = [x - (x3 - x1) / 6, y - (y3 - y1) / 6]
    return `${d}C${f(c1[0])} ${f(c1[1])} ${f(c2[0])} ${f(c2[1])} ${f(x)} ${f(y)}`
  }, '')
}

/* print volume over the months, drawing itself on the left wall screen */
const LineChart: FunctionComponent<{ values?: number[] }> = ({ values }) => {
  const data = values && values.length > 1 ? values : SAMPLE_LINE
  const high = Math.max(...data)
  const low = Math.min(...data)
  // gentle: the swings use the middle of the screen (the axis starts well below the lowest month), and the
  // line is a smooth curve through the months rather than a zigzag
  const range = high - low || high || 1
  const floor = low - range * 1.2
  const ceiling = high + range * 0.4
  const points = data.map((v, i) => [
    LINE_SCREEN.x + PAD + (i / (data.length - 1)) * (SCREEN_W - PAD * 2),
    LINE_SCREEN.y + PAD + (1 - (v - floor) / (ceiling - floor)) * (SCREEN_H - PAD * 2),
  ])
  const d = smoothPath(points)
  const [lx, ly] = points[points.length - 1]
  const base = LINE_SCREEN.y + SCREEN_H - PAD
  return (
    <g>
      <path d={`${d}L${lx.toFixed(1)} ${base}H${LINE_SCREEN.x + PAD}Z`} className={styles.lineArea} />
      <path d={d} pathLength={100} className={styles.line} />
      <circle cx={lx} cy={ly} r='7' className={styles.lineDot} />
    </g>
  )
}

/* fleet value by lifecycle stage, as bars growing on the right wall screen (a stage worth nothing has no bar) */
const BarChart: FunctionComponent<{ values?: number[] }> = ({ values }) => {
  const real = (values ?? []).filter(v => v > 0)
  const data = real.length > 0 ? real : SAMPLE_BARS
  const high = Math.max(...data)
  const gap = 8
  const w = (SCREEN_W - PAD * 2 - gap * (data.length - 1)) / data.length
  const base = BAR_SCREEN.y + SCREEN_H - PAD
  return (
    <g>
      {data.map((v, i) => {
        const height = Math.max(0.12, v / high) * (SCREEN_H - PAD * 2)
        return (
          <rect
            // eslint-disable-next-line react/no-array-index-key -- bars in a fixed order
            key={i}
            x={BAR_SCREEN.x + PAD + i * (w + gap)}
            y={base - height}
            width={w}
            height={height}
            rx='3'
            className={`${styles.bar} ${i % 2 ? styles.barDim : ''} ${styles[`b${i % 5}`]}`}
          />
        )
      })}
    </g>
  )
}

/* code being written on the desk screen at the right: skeleton lines typing in */
const CodeScreen: FunctionComponent = () => {
  const lineH = 8
  const step = 14
  return (
    <g>
      {CODE_LINES.map((line, i) => {
        let x = CODE_SCREEN.x + 18 + line.indent
        const y = CODE_SCREEN.y + 18 + i * step
        return (
          // eslint-disable-next-line react/no-array-index-key -- a fixed listing
          <g key={i} className={`${styles.codeLine} ${styles[`l${i}`]}`}>
            {line.tokens.map((w, t) => {
              const token = (
                <rect
                  // eslint-disable-next-line react/no-array-index-key -- a fixed listing
                  key={t}
                  x={x}
                  y={y}
                  width={w}
                  height={lineH}
                  rx='3'
                  className={TOKEN_SHADES[t % TOKEN_SHADES.length]}
                />
              )
              x += w + 6
              return token
            })}
          </g>
        )
      })}
    </g>
  )
}

/* a warning sign on the robot's screen at the left, for the customers at risk, blinking (it moves with the
   robot's hand) */
const WarningScreen: FunctionComponent = () => {
  const cx = TABLET.x + TABLET.w / 2
  const size = 34
  const h = size * 0.87
  const top = TABLET.y + (TABLET.h - h) / 2
  return (
    <g className={styles.tablet}>
      <g className={styles.warning}>
        <path d={`M${cx} ${top}L${cx + size / 2} ${top + h}H${cx - size / 2}Z`} className={styles.warningSign} />
        <rect x={cx - 2.25} y={top + 9} width='4.5' height='11' rx='2.25' className={styles.warningMark} />
        <circle cx={cx} cy={top + h - 5} r='2.5' className={styles.warningMark} />
      </g>
    </g>
  )
}

/* the inside of the ship's curved hull, faint behind the whole banner (drawn by the banner, edge to edge), as a
   grid of panel seams: lines along it, arching at the top, flattening through the middle and dipping at the
   bottom, crossed by its ribs, evenly spaced, bowing out more the further they are from the middle, as the hull
   curves round. Stretched to the banner, so their lines keep the same thin width. */
const SEAM_COUNT = 9
const HULL_SEAMS = Array.from({ length: SEAM_COUNT }, (_, i) => {
  const u = i / (SEAM_COUNT - 1) // 0 at the top, 1 at the bottom
  const y = 34 + u * (278 - 34)
  const bow = -34 + u * (34 + 18) // arching up at the top, down at the bottom
  return `M0 ${y.toFixed(1)}C300 ${(y + bow).toFixed(1)} 700 ${(y + bow).toFixed(1)} 1000 ${y.toFixed(1)}`
})
const RIB_COUNT = 24
const HULL_RIBS = Array.from({ length: RIB_COUNT }, (_, i) => {
  const x = ((i + 0.5) / RIB_COUNT) * 1000
  const bow = ((x - 500) / 500) * 28
  return `M${x.toFixed(1)} 0C${(x + bow).toFixed(1)} 100 ${(x + bow).toFixed(1)} 200 ${x.toFixed(1)} 300`
})

export const ColonyHull: FunctionComponent = () => (
  <svg viewBox='0 0 1000 300' preserveAspectRatio='none' aria-hidden='true' className={styles.hull}>
    {HULL_SEAMS.map(d => (
      <path key={d} d={d} className={styles.hullLine} />
    ))}
    {HULL_RIBS.map(d => (
      <path key={d} d={d} className={styles.hullLine} />
    ))}
  </svg>
)

export const ColonyScene: FunctionComponent<Props> = ({ stats, onDone }) => {
  useEffect(() => {
    if (prefersReducedMotion()) return undefined
    const timer = window.setTimeout(onDone, SEQUENCE_MS)
    return () => window.clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [left, right] = statsOf(stats)

  return (
    <div className={styles.layout}>
      <div className={`${styles.side} ${styles.sideLeft}`}>
        {left.map((s, i) => (
          <StatBlock key={s.key} stat={s} index={i} />
        ))}
      </div>

      <svg
        viewBox={`0 0 ${ART_W} ${ART_H}`}
        role='img'
        aria-label='The crew of a space station at its three windows, watching over print volume, risk, fleet value and devices'
        className={styles.svg}
      >
        <defs>
          <clipPath id='colony-windows'>
            {WINDOWS.map(d => (
              <path key={d.slice(0, 16)} d={d} />
            ))}
          </clipPath>
          <linearGradient id='colony-trail' x1='0' y1='0' x2='1' y2='0'>
            <stop offset='0' stopColor='#f9ddbf' stopOpacity='0' />
            <stop offset='1' stopColor='#f9ddbf' />
          </linearGradient>
        </defs>

        <ColonyArt x='0' y='0' width={ART_W} height={ART_H} className={styles.art} aria-hidden='true' />

        {/* a shooting star, crossing all three windows (hidden by the walls between them) */}
        <g clipPath='url(#colony-windows)'>
          <g className={styles.shootingStar}>
            <path d='M-300 -14L0 0' className={styles.trail} />
            <circle cx='0' cy='0' r='6' className={styles.starHead} />
          </g>
        </g>

        <LineChart values={stats?.printVolumeTrend} />
        <BarChart values={stats?.fleetValueByStage} />
        <CodeScreen />
        <WarningScreen />
      </svg>

      <div className={`${styles.side} ${styles.sideRight}`}>
        {right.map((s, i) => (
          <StatBlock key={s.key} stat={s} index={i + 2} />
        ))}
      </div>
    </div>
  )
}
